/**
 * EngagementConfigPage — Story 3.6: Touch Cadence Engine & Clinical Parameter Configuration
 *
 * Admin UI for configuring Betty's engagement parameters:
 *   - Gap Return Threshold
 *   - Escalation Thresholds
 *   - Touch Frequency
 *
 * Requires staff:admin role. All changes take effect immediately for new member interactions.
 *
 * @NonPhiEndpoint — clinical parameters only, no member PHI.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EngagementConfigData {
  escalationThresholdMissed: number;
  rdEscalationThreshold?: number | null;
  highTouchFrequencyDays: number;
  lowTouchFrequencyDays: number;
  gapReturnThresholdDays: number;
  updatedBy?: string | null;
  updatedAt?: string | null;
  version: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EngagementConfigPage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token;

  const [config, setConfig] = useState<EngagementConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Gap return threshold form
  const [gapThresholdDays, setGapThresholdDays] = useState('');
  const [gapSaving, setGapSaving] = useState(false);
  const [gapError, setGapError] = useState<string | null>(null);
  const [gapSuccess, setGapSuccess] = useState(false);

  // Escalation thresholds form
  const [escalationMissed, setEscalationMissed] = useState('');
  const [rdThreshold, setRdThreshold] = useState('');
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [escalationError, setEscalationError] = useState<string | null>(null);
  const [escalationSuccess, setEscalationSuccess] = useState(false);

  // Touch frequency form
  const [highTouchDays, setHighTouchDays] = useState('');
  const [lowTouchDays, setLowTouchDays] = useState('');
  const [touchSaving, setTouchSaving] = useState(false);
  const [touchError, setTouchError] = useState<string | null>(null);
  const [touchSuccess, setTouchSuccess] = useState(false);

  useEffect(() => {
    void fetchConfig();
  }, [auth]);

  async function fetchConfig(): Promise<void> {
    if (!accessToken) {
      setFetchError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${SERVICES_BASE_URL}/v1/engagement/admin/v1/config`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) {
        setFetchError(`Failed to load config (${res.status})`);
        return;
      }
      const data = (await res.json()) as EngagementConfigData;
      setConfig(data);
      // Pre-fill form inputs with current values
      setGapThresholdDays(String(data.gapReturnThresholdDays));
      setEscalationMissed(String(data.escalationThresholdMissed));
      setRdThreshold(data.rdEscalationThreshold != null ? String(data.rdEscalationThreshold) : '');
      setHighTouchDays(String(data.highTouchFrequencyDays));
      setLowTouchDays(String(data.lowTouchFrequencyDays));
    } catch {
      setFetchError('Unable to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGapThresholdSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!accessToken) return;
    setGapSaving(true);
    setGapError(null);
    setGapSuccess(false);

    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/config/gap-return-threshold-days`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ thresholdDays: parseInt(gapThresholdDays, 10) }),
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setGapError(`Update failed (${res.status}): ${errBody.detail ?? 'Unknown error'}`);
        return;
      }
      const updated = (await res.json()) as EngagementConfigData;
      setConfig(updated);
      setGapSuccess(true);
    } catch {
      setGapError('Unable to connect to API.');
    } finally {
      setGapSaving(false);
    }
  }

  async function handleEscalationSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!accessToken) return;
    setEscalationSaving(true);
    setEscalationError(null);
    setEscalationSuccess(false);

    const body: Record<string, number> = {};
    if (escalationMissed.trim()) body['consecutiveMissedCheckins'] = parseInt(escalationMissed, 10);
    if (rdThreshold.trim()) body['rdEscalationThreshold'] = parseInt(rdThreshold, 10);

    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/config/escalation-thresholds`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setEscalationError(`Update failed (${res.status}): ${errBody.detail ?? 'Unknown error'}`);
        return;
      }
      const updated = (await res.json()) as EngagementConfigData;
      setConfig(updated);
      setEscalationSuccess(true);
    } catch {
      setEscalationError('Unable to connect to API.');
    } finally {
      setEscalationSaving(false);
    }
  }

  async function handleTouchFrequencySave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!accessToken) return;
    setTouchSaving(true);
    setTouchError(null);
    setTouchSuccess(false);

    const body: Record<string, number> = {};
    if (highTouchDays.trim()) body['highTouchFrequencyDays'] = parseInt(highTouchDays, 10);
    if (lowTouchDays.trim()) body['lowTouchFrequencyDays'] = parseInt(lowTouchDays, 10);

    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/config/touch-frequency`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setTouchError(`Update failed (${res.status}): ${errBody.detail ?? 'Unknown error'}`);
        return;
      }
      const updated = (await res.json()) as EngagementConfigData;
      setConfig(updated);
      setTouchSuccess(true);
    } catch {
      setTouchError('Unable to connect to API.');
    } finally {
      setTouchSaving(false);
    }
  }

  function renderAuditMeta(): JSX.Element | null {
    if (!config || config.version === 0) return null;
    return (
      <p className="text-xs text-gray-400 mt-1">
        v{config.version} · Updated by {config.updatedBy ?? '—'} at{' '}
        {config.updatedAt != null ? new Date(config.updatedAt).toLocaleString() : '—'}
      </p>
    );
  }

  return (
    <div className="p-8 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Engagement Configuration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Tune Betty's engagement parameters for the Clinical Team. Changes take effect immediately
          for new member interactions.
        </p>
      </div>

      {/* Compliance banner */}
      <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-sm text-amber-800">
          <strong>Clinical Team notice:</strong> Engagement parameter changes take effect
          immediately for new member interactions. Coordinate changes with the full Clinical Team.
        </p>
      </div>

      {loading && <p className="mt-6 text-gray-400">Loading configuration…</p>}
      {fetchError !== null && <p className="mt-6 text-red-600">{fetchError}</p>}

      {!loading && fetchError === null && config !== null && (
        <div className="mt-6 space-y-8">
          {/* Current config summary */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Current Configuration</h2>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-gray-500">Gap Return Threshold</dt>
              <dd className="font-medium">{config.gapReturnThresholdDays} days</dd>
              <dt className="text-gray-500">Escalation (consecutive missed)</dt>
              <dd className="font-medium">{config.escalationThresholdMissed}</dd>
              <dt className="text-gray-500">RD Escalation Threshold</dt>
              <dd className="font-medium">{config.rdEscalationThreshold ?? '—'}</dd>
              <dt className="text-gray-500">High-Touch Frequency</dt>
              <dd className="font-medium">{config.highTouchFrequencyDays} days</dd>
              <dt className="text-gray-500">Low-Touch Frequency</dt>
              <dd className="font-medium">{config.lowTouchFrequencyDays} days</dd>
            </dl>
            {renderAuditMeta()}
          </div>

          {/* Gap Return Threshold form */}
          <section>
            <h2 className="text-base font-semibold text-gray-800">Gap Return Threshold</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Days since last check-in before a member is considered a "gap return".
            </p>
            <form
              onSubmit={(e) => void handleGapThresholdSave(e)}
              className="mt-3 flex items-end gap-3"
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Threshold Days (1–30)
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  max={30}
                  value={gapThresholdDays}
                  onChange={(e) => setGapThresholdDays(e.target.value)}
                  className="block w-28 border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5"
                />
              </div>
              <button
                type="submit"
                disabled={gapSaving}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {gapSaving ? 'Saving…' : 'Save'}
              </button>
            </form>
            {gapError !== null && <p className="mt-1 text-sm text-red-600">{gapError}</p>}
            {gapSuccess && <p className="mt-1 text-sm text-green-600">Saved successfully.</p>}
          </section>

          {/* Escalation Thresholds form */}
          <section>
            <h2 className="text-base font-semibold text-gray-800">Escalation Thresholds</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Consecutive missed check-ins before tier escalation and optional RD alert.
            </p>
            <form onSubmit={(e) => void handleEscalationSave(e)} className="mt-3 space-y-3">
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Consecutive Missed Check-ins (1–30)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={escalationMissed}
                    onChange={(e) => setEscalationMissed(e.target.value)}
                    className="block w-28 border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    RD Escalation Threshold (1–30, optional)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={rdThreshold}
                    onChange={(e) => setRdThreshold(e.target.value)}
                    className="block w-28 border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5"
                    placeholder="—"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={escalationSaving}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {escalationSaving ? 'Saving…' : 'Save'}
              </button>
            </form>
            {escalationError !== null && (
              <p className="mt-1 text-sm text-red-600">{escalationError}</p>
            )}
            {escalationSuccess && (
              <p className="mt-1 text-sm text-green-600">Saved successfully.</p>
            )}
          </section>

          {/* Touch Frequency form */}
          <section>
            <h2 className="text-base font-semibold text-gray-800">Touch Frequency</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Check-in frequency for high-touch and low-touch members.
            </p>
            <p className="text-xs text-amber-700 mt-0.5 font-medium">
              Floor: 1 day. Ceiling: 30 days. Hard caps enforced server-side.
            </p>
            <form onSubmit={(e) => void handleTouchFrequencySave(e)} className="mt-3 space-y-3">
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    High-Touch Frequency Days (1–30)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={highTouchDays}
                    onChange={(e) => setHighTouchDays(e.target.value)}
                    className="block w-28 border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Low-Touch Frequency Days (1–30)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={lowTouchDays}
                    onChange={(e) => setLowTouchDays(e.target.value)}
                    className="block w-28 border border-gray-300 rounded-md shadow-sm text-sm px-3 py-1.5"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={touchSaving}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {touchSaving ? 'Saving…' : 'Save'}
              </button>
            </form>
            {touchError !== null && <p className="mt-1 text-sm text-red-600">{touchError}</p>}
            {touchSuccess && <p className="mt-1 text-sm text-green-600">Saved successfully.</p>}
          </section>
        </div>
      )}
    </div>
  );
}
